// @flow

import { useEffect } from 'react';

import { selectFromConfig } from '@gandi/redux-config';

import { useAppSelector } from 'src/hooks/useAppSelector';

import * as cookies from '../modules/cookies';
import { initGtm, updateGoogleConsent } from '../modules/tracking/gtm';
import { setPianoMode } from '../modules/tracking/pianoAnalytics';

import './tarteaucitron.css';

const selectCmpConfig = selectFromConfig<{
  id: void | string,
  enabled: boolean,
  domain?: string,
}>('tarteaucitron', {});

const selectGtmId = selectFromConfig<string>('gtm.id', '');

const selectAppEndpoint = selectFromConfig<string>('app.endpoint', '/');

const ADS_URL_PARAMS = [
  'gclid',
  'fbclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  '_fbc',
  '_fbp',
  '_gcl_aw',
];

const ADS_COOKIES = ['_fbp', '_fbc', '_gcl_aw', '_gcl_au'];

// Forwarded tracking cookies from Gandi Studio to persist
const FORWARDED_COOKIES = [
  '_fbc',
  '_fbp',
  '_gcl_au',
  '_gcl_aw',
  'pa_privacy',
  'tarteaucitron',
  '_pprv',
];

export function useAffiliateAttribution() {
  const {
    id: cmpId,
    enabled: cmpEnabled,
    domain: configTcDomain,
  } = useAppSelector(selectCmpConfig);
  const gtmId = useAppSelector(selectGtmId);
  const appEndpoint = useAppSelector(selectAppEndpoint).replace(/\/$/, '');
  const cjAttributionEndpoint = `${appEndpoint}/api/v5/cj-attribution-middleware/`;

  useEffect(() => {
    const hostname = window.location.hostname;

    const tcDomain = configTcDomain || hostname;
    const cookieDomain = hostname ? `.${hostname}` : undefined;
    const urlParams = new URLSearchParams(window.location.search);

    // Save forwarded cookies from Gandi Studio and clean URL query string
    let hasForwardedParams = false;
    const expiration = new Date(Date.now() + 395 * 86400000).toUTCString();

    FORWARDED_COOKIES.forEach(cookieName => {
      const value = urlParams.get(cookieName);
      if (value) {
        hasForwardedParams = true;
        cookies.setCookie(cookieName, {
          value,
          domain: cookieDomain,
          expires: expiration,
        });
        urlParams.delete(cookieName);
      }
    });

    if (hasForwardedParams) {
      const url = new URL(window.location.href);
      url.search = urlParams.toString();
      window.history.replaceState(null, '', url);
    }

    // 1. Detection of CJ intents
    const initialCjevent = urlParams.get('cjevent');
    const hasInitialCj = urlParams.has('cjevent');
    const hasCjCookie = cookies.hasCookie('cje');
    const shouldLoadCj = hasInitialCj || hasCjCookie;

    // 2. Detection of ad intent (Google / Meta / Gandi Studio)
    const hasInitialAds = ADS_URL_PARAMS.some(p => urlParams.has(p));
    const hasAdsCookie = ADS_COOKIES.some(c => cookies.hasCookie(c));
    const hasTacCookie = cookies.hasCookie('tarteaucitron');
    const shouldLoadGtm = Boolean(
      gtmId && (hasInitialAds || hasAdsCookie || hasTacCookie),
    );

    // If no third-party tracking service is triggered, nothing is loaded.
    const shouldLoadAnyCmp = shouldLoadCj || shouldLoadGtm || hasTacCookie;
    if (!shouldLoadAnyCmp) return;

    // Immediate loading of the GTM script in 'denied' mode if Ads are present
    if (shouldLoadGtm && gtmId) {
      initGtm(gtmId);
    }

    window.tarteaucitronCustomPremium = () => {
      if (!window.tarteaucitron) return;

      window.tarteaucitron.services = window.tarteaucitron.services || {};
      window.tarteaucitron.job = window.tarteaucitron.job || [];

      // Service 1 : CJ Affiliate
      if (shouldLoadCj) {
        window.tarteaucitron.services.cj = {
          key: 'cj',
          type: 'ads',
          name: 'CJ Affiliate',
          uri: 'https://www.cj.com/legal/privacy',
          needConsent: true,
          cookies: ['cje'],
          js() {
            if (initialCjevent) {
              fetch(
                `${cjAttributionEndpoint}?cjevent=${encodeURIComponent(initialCjevent)}`,
                { method: 'GET', credentials: 'include' },
              ).catch(() => {
                cookies.setCookie('cje', {
                  value: initialCjevent,
                  expires: new Date(Date.now() + 395 * 86400000).toDateString(),
                  domain: cookieDomain,
                });
              });
            }
          },
          fallback() {
            fetch(cjAttributionEndpoint, {
              method: 'DELETE',
              credentials: 'include',
            }).catch(() => {
              cookies.setCookie('cje', { domain: cookieDomain });
            });
          },
        };

        if (!window.tarteaucitron.job.includes('cj')) {
          window.tarteaucitron.job.push('cj');
        }
      }

      // Service 2 : Google Tag Manager (Google Ads, Meta Ads)
      if (shouldLoadGtm && gtmId) {
        window.tarteaucitron.user = window.tarteaucitron.user || {};
        window.tarteaucitron.user.googletagmanagerId = gtmId;

        window.tarteaucitron.services.googletagmanager = {
          key: 'googletagmanager',
          type: 'ads',
          name: 'Google Tag Manager (Google Ads, Meta)',
          uri: 'https://policies.google.com/privacy',
          needConsent: true,
          cookies: ['_gcl_au', '_fbp'],
          js() {
            updateGoogleConsent('granted');
          },
          fallback() {
            updateGoogleConsent('denied');
          },
        };

        if (!window.tarteaucitron.job.includes('googletagmanager')) {
          window.tarteaucitron.job.push('googletagmanager');
        }
      }

      // Service 3 : Piano Analytics (Opt-in promotion)
      window.tarteaucitron.services.pianoanalytics = {
        key: 'pianoanalytics',
        type: 'analytic',
        name: 'Mesure d’audience (Piano)',
        uri: 'https://piano.io/privacy-policy/',
        needConsent: true,
        cookies: ['_pprv', 'pa_privacy'],
        js() {
          setPianoMode('optin');
        },
        fallback() {
          setPianoMode('exempt');
        },
      };

      if (!window.tarteaucitron.job.includes('pianoanalytics')) {
        window.tarteaucitron.job.push('pianoanalytics');
      }
    };

    // Dynamic loading of the Tarteaucitron script
    const scriptId = 'tarteaucitron-script';
    if (document.getElementById(scriptId)) {
      window.tarteaucitronCustomPremium();
    } else if (cmpEnabled && cmpId) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://tarteaucitron.io/load.js?domain=${tcDomain}&uuid=${cmpId}`;
      script.async = true;
      document.body?.appendChild(script);
    }
  }, [cjAttributionEndpoint, cmpEnabled, cmpId, configTcDomain, gtmId]);
}
